import Decimal from 'decimal.js';

import { SimplePieChart } from '@/components/charts/PieChart/SimplePieChart';
import {
  formatCurrency,
  formatCurrencyNoDecimals,
} from '@/utils/formatting/currency';

export const COMPACT_CURRENCY_OPTIONS: Intl.NumberFormatOptions = {
  notation: 'compact' as const,
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
  currencySign: 'accounting' as const,
};

export interface HouseholdAssetsSummary {
  liquidValue: Decimal;
  liquidPercentage: Decimal;
  inEstateLiquidValue: Decimal;
  inEstateLiquidPercentage: Decimal;
  outOfEstateLiquidValue: Decimal;
  outOfEstateLiquidPercentage: Decimal;
}

export function formatCompactCurrency(value: Decimal | undefined): string {
  const resolved = value ?? new Decimal(0);
  if (resolved.isZero()) return '$0';
  return formatCurrency(resolved, {
    notation: 'compact' as const,
    currencySign: 'accounting' as const,
  });
}

export function formatLiquidityLabel(
  liquidValue: Decimal,
  liquidPercentage: Decimal
): string {
  if (liquidValue.isZero()) {
    return '$0 liquid (0%)';
  }

  const formattedValue = formatCurrency(
    liquidValue,
    { notation: 'compact' as const },
    { showCents: true }
  );

  let formattedPercent: string;
  if (liquidPercentage.isZero()) {
    formattedPercent = '0%';
  } else if (liquidPercentage.lessThan(1)) {
    formattedPercent = '<1%';
  } else {
    formattedPercent = `${liquidPercentage.round().toString()}%`;
  }

  return `${formattedValue} liquid (${formattedPercent})`;
}

interface EstateValuePieChartProps {
  pieChartSeriesData: {
    value: number;
    label: string;
    color: string;
  }[];
  onePercentOfSum: Decimal;
  size?: number;
}

export function EstateValuePieChart({
  pieChartSeriesData,
  onePercentOfSum,
  size = 60,
}: EstateValuePieChartProps) {
  return (
    <SimplePieChart
      size={size}
      series={[
        {
          valueFormatter: (r) => {
            if (r.value === onePercentOfSum.toNumber()) {
              // We modify the series data when the slice is less than or equal to 1% so the displayed slice is equal
              // to the value of one percent of the sum of the three values. We lose the original value of the slice
              // so we display <1% in the tooltip.
              return '<1%';
            }

            return formatCurrencyNoDecimals(new Decimal(r.value));
          },
          data: pieChartSeriesData,
        },
      ]}
    />
  );
}
